import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { BulkJobListItem, BulkJobStatus } from '@designdeck/shared';
import { bulkKeys, useBulkJobs } from '@/hooks/useBulkCreation';
import { designKeys } from '@/hooks/useDesigns';
import { reviewKeys } from '@/hooks/useReviewQueue';
import { cn } from '@/lib/utils';
import { BulkJobCard } from './BulkJobCard';

function isActive(status: BulkJobStatus): boolean {
  return status === 'QUEUED' || status === 'RUNNING';
}

/**
 * Refreshes what a finished run changed: the design grid, the Review nav
 * count and the job's own report (all pages instead of the first). Only a
 * transition seen on this page counts; jobs that were already terminal on
 * the first load changed nothing since the grid loaded.
 */
function useRefreshOnJobFinish(jobs: readonly BulkJobListItem[] | undefined) {
  const queryClient = useQueryClient();
  const previous = useRef(new Map<string, BulkJobStatus>());

  useEffect(() => {
    if (!jobs) return;
    let finished = false;
    for (const job of jobs) {
      const before = previous.current.get(job.id);
      if (before !== undefined && isActive(before) && !isActive(job.status)) {
        finished = true;
        void queryClient.invalidateQueries({ queryKey: bulkKeys.job(job.id) });
      }
      previous.current.set(job.id, job.status);
    }
    if (finished) {
      void queryClient.invalidateQueries({ queryKey: designKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: reviewKeys.all });
    }
  }, [jobs, queryClient]);
}

export interface BulkJobCardsProps {
  className?: string;
}

/**
 * The progress cards of the seller's bulk runs above the design grid, one
 * per job that was not dismissed, newest first. Renders nothing without
 * jobs, so the overview keeps its layout for sellers who never ran one.
 */
export function BulkJobCards({ className }: BulkJobCardsProps) {
  const { data: jobs } = useBulkJobs();
  useRefreshOnJobFinish(jobs);

  if (!jobs || jobs.length === 0) return null;

  const sorted = [...jobs].sort(
    (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)
  );

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {sorted.map((job) => (
        <BulkJobCard key={job.id} job={job} />
      ))}
    </div>
  );
}
